import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Table,
  Button,
  Modal,
  Input,
  Space,
  Tag,
  Badge,
  Progress,
  Empty,
  Spin,
  message,
  Typography,
  Tooltip,
  Form,
} from 'antd';
import {
  GiftOutlined,
  CopyOutlined,
  FacebookOutlined,
  TwitterOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { copyToClipboard } from '../utils/clipboard';

const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface RewardBalance {
  customerId: number;
  currentPoints: number;
  currentTier: string;
  nextTier: string;
  pointsToNextTier: number;
  tierMultiplier: number;
  lifetimePointsEarned: number;
}

interface RewardHistory {
  items: Array<{
    id: number;
    date: string;
    description: string;
    type: 'earned' | 'redeemed' | 'adjusted';
    points: number;
    balanceAfter: number;
  }>;
  total: number;
  page: number;
  limit: number;
}

interface Referral {
  id: number;
  referred_customer_id: number;
  referred_email: string;
  status: string;
  created_at: string;
  referral_bonus_earned: number;
}

const getTierColor = (tier: string): string => {
  const colors: { [key: string]: string } = {
    Bronze: '#CD7F32',
    Silver: '#C0C0C0',
    Gold: '#FFD700',
    Platinum: '#E5E4E2',
  };
  return colors[tier] || '#999';
};

const CustomerRewards: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, accessToken } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<RewardBalance | null>(null);
  const [history, setHistory] = useState<RewardHistory | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [referralCode, setReferralCode] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [redeemModalVisible, setRedeemModalVisible] = useState(false);
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const loadRewardsData = React.useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };

      const [balanceRes, historyRes, referralCodeRes, referralsRes] = await Promise.all([
        axios.get(`${API_URL}/api/rewards/balance`, { headers }),
        axios.get(`${API_URL}/api/rewards/history?page=1&limit=20`, { headers }),
        axios.get(`${API_URL}/api/rewards/referral-code`, { headers }),
        axios.get(`${API_URL}/api/rewards/referrals`, { headers }),
      ]);

      setBalance(balanceRes.data);
      setHistory(historyRes.data);
      setReferralCode(referralCodeRes.data.referralCode);
      setReferrals(referralsRes.data);
    } catch (error) {
      console.error('Error loading rewards data:', error);
      message.error('Failed to load rewards data');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    loadRewardsData();
  }, [isAuthenticated, navigate, loadRewardsData]);

  const handleRedeemPoints = async () => {
    if (!redeemAmount || parseInt(redeemAmount) <= 0) {
      message.error('Please enter a valid amount');
      return;
    }

    if (balance && parseInt(redeemAmount) > balance.currentPoints) {
      message.error('Insufficient points');
      return;
    }

    setRedeemLoading(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      await axios.post(
        `${API_URL}/api/rewards/redeem`,
        { estimateId: 0, points: parseInt(redeemAmount) },
        { headers }
      );

      message.success('Points redeemed successfully!');
      setRedeemModalVisible(false);
      setRedeemAmount('');
      loadRewardsData();
    } catch (error: any) {
      console.error('Error redeeming points:', error);
      message.error(error.response?.data?.error || 'Failed to redeem points');
    } finally {
      setRedeemLoading(false);
    }
  };

  const handleCopyReferralCode = async () => {
    const success = await copyToClipboard(referralCode);
    if (success) {
      message.success('Referral code copied!');
    } else {
      message.error('Failed to copy referral code');
    }
  };

  const handleShareReferral = (platform: string) => {
    const referralUrl = `${window.location.origin}?ref=${referralCode}`;
    const text = `Join me and earn rewards! Use code ${referralCode}`;

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}`, '_blank');
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralUrl)}`,
          '_blank'
        );
        break;
      case 'copy':
        copyToClipboard(referralUrl).then((success) => {
          if (success) message.success('Share link copied!');
          else message.error('Failed to copy share link');
        });
        break;
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  const progressPercent = balance
    ? ((balance.currentPoints / (balance.currentPoints + balance.pointsToNextTier)) * 100)
    : 0;

  const historyColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
      width: 120,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        const colorMap: { [key: string]: string } = {
          earned: 'green',
          redeemed: 'orange',
          adjusted: 'blue',
        };
        return <Tag color={colorMap[type] || 'default'}>{type}</Tag>;
      },
      width: 100,
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      render: (points: number, record: any) => (
        <Text strong style={{ color: record.type === 'earned' ? 'green' : 'red' }}>
          {record.type === 'earned' ? '+' : '-'}{Math.abs(points)}
        </Text>
      ),
      width: 100,
    },
    {
      title: 'Balance After',
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      width: 120,
    },
  ];

  const referralColumns = [
    {
      title: 'Email',
      dataIndex: 'referred_email',
      key: 'referred_email',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig: { [key: string]: any } = {
          pending: { icon: <ClockCircleOutlined />, color: 'processing' },
          completed: { icon: <CheckCircleOutlined />, color: 'success' },
        };
        const config = statusConfig[status] || { icon: null, color: 'default' };
        return <Tag icon={config.icon} color={config.color}>{status}</Tag>;
      },
    },
    {
      title: 'Bonus Earned',
      dataIndex: 'referral_bonus_earned',
      key: 'referral_bonus_earned',
      render: (bonus: number) => <Text strong>{bonus} pts</Text>,
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
    },
  ];

  return (
    <PublicLayout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
          padding: '40px 20px',
        }}
      >
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Title level={3} style={{ margin: 0 }}>
                <GiftOutlined /> Your Rewards
              </Title>
              <Text type="secondary">Earn and redeem points for your loyalty</Text>
            </Space>
          </Card>

          {/* Points Balance Card */}
          <Card
            style={{
              marginBottom: 24,
              background: `linear-gradient(135deg, ${getTierColor(balance?.currentTier || 'Bronze')}20 0%, transparent 100%)`,
              borderColor: getTierColor(balance?.currentTier || 'Bronze'),
            }}
          >
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      CURRENT POINTS BALANCE
                    </Text>
                    <div style={{ fontSize: 48, fontWeight: 'bold', color: getTierColor(balance?.currentTier || 'Bronze') }}>
                      {balance?.currentPoints.toLocaleString() || 0}
                    </div>
                  </div>

                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      TIER
                    </Text>
                    <div>
                      <Badge
                        color={getTierColor(balance?.currentTier || 'Bronze')}
                        text={
                          <Text strong style={{ fontSize: 18 }}>
                            {balance?.currentTier || 'Bronze'}
                          </Text>
                        }
                      />
                    </div>
                  </div>

                  <div>
                    <Space>
                      <Text type="secondary">Multiplier:</Text>
                      <Text strong>{balance?.tierMultiplier || 1}x</Text>
                    </Space>
                  </div>
                </Space>
              </Col>

              <Col xs={24} md={12}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      PROGRESS TO NEXT TIER
                    </Text>
                    <Progress
                      percent={Math.min(progressPercent, 100)}
                      strokeColor={getTierColor(balance?.currentTier || 'Bronze')}
                      format={() => `${balance?.currentPoints || 0} / ${(balance?.currentPoints || 0) + (balance?.pointsToNextTier || 0)}`}
                    />
                  </div>

                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      LIFETIME EARNED
                    </Text>
                    <Text strong style={{ fontSize: 18 }}>
                      {balance?.lifetimePointsEarned.toLocaleString() || 0}
                    </Text>
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    onClick={() => setRedeemModalVisible(true)}
                    block
                  >
                    Redeem Points
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {/* Quick Stats */}
            <Col xs={24} sm={12} md={6}>
              <Card>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    This Month
                  </Text>
                  <div style={{ fontSize: 24, fontWeight: 'bold' }}>
                    {history?.items.filter(
                      (item) =>
                        item.type === 'earned' &&
                        dayjs(item.date).isAfter(dayjs().startOf('month'))
                    ).reduce((sum, item) => sum + item.points, 0) || 0}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    points earned
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Card>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Lifetime
                  </Text>
                  <div style={{ fontSize: 24, fontWeight: 'bold' }}>
                    {balance?.lifetimePointsEarned.toLocaleString() || 0}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    total earned
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Card>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Tier Bonus
                  </Text>
                  <div style={{ fontSize: 24, fontWeight: 'bold' }}>
                    {((balance?.tierMultiplier || 1) - 1) * 100}%
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    multiplier
                  </Text>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Card>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    To Next Tier
                  </Text>
                  <div style={{ fontSize: 24, fontWeight: 'bold' }}>
                    {balance?.pointsToNextTier.toLocaleString() || 0}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    points needed
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>

          {/* Transaction History */}
          <Card
            title={
              <Space>
                <span>Transaction History</span>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            {history && history.items.length > 0 ? (
              <Table
                columns={historyColumns}
                dataSource={history.items.map((item, index) => ({
                  ...item,
                  key: item.id || index,
                }))}
                pagination={{
                  current: currentPage,
                  pageSize: 20,
                  total: history.total,
                  onChange: (page) => setCurrentPage(page),
                }}
                size="small"
              />
            ) : (
              <Empty description="No transaction history" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          {/* Referral Section */}
          <Card title="Share & Earn Referral Bonus" style={{ marginBottom: 24 }}>
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      YOUR REFERRAL CODE
                    </Text>
                    <Space style={{ width: '100%', marginTop: 8 }}>
                      <Input
                        value={referralCode}
                        disabled
                        style={{ flex: 1 }}
                      />
                      <Tooltip title="Copy code">
                        <Button
                          icon={<CopyOutlined />}
                          onClick={handleCopyReferralCode}
                        />
                      </Tooltip>
                    </Space>
                  </div>

                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      SHARE VIA
                    </Text>
                    <Space style={{ marginTop: 8 }}>
                      <Button
                        icon={<FacebookOutlined />}
                        onClick={() => handleShareReferral('facebook')}
                      >
                        Facebook
                      </Button>
                      <Button
                        icon={<TwitterOutlined />}
                        onClick={() => handleShareReferral('twitter')}
                      >
                        Twitter
                      </Button>
                      <Button
                        icon={<LinkOutlined />}
                        onClick={() => handleShareReferral('copy')}
                      >
                        Copy Link
                      </Button>
                    </Space>
                  </div>
                </Space>
              </Col>

              <Col xs={24} md={12}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    YOUR REFERRALS ({referrals.length})
                  </Text>
                  {referrals.length > 0 ? (
                    <Table
                      columns={referralColumns}
                      dataSource={referrals.map((ref, index) => ({
                        ...ref,
                        key: ref.id || index,
                      }))}
                      pagination={false}
                      size="small"
                      style={{ marginTop: 12 }}
                    />
                  ) : (
                    <Empty
                      description="No referrals yet"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      style={{ marginTop: 12 }}
                    />
                  )}
                </div>
              </Col>
            </Row>
          </Card>
        </div>
      </div>

      {/* Redeem Modal */}
      <Modal
        title="Redeem Points"
        open={redeemModalVisible}
        onOk={handleRedeemPoints}
        onCancel={() => {
          setRedeemModalVisible(false);
          setRedeemAmount('');
        }}
        loading={redeemLoading}
      >
        <Form layout="vertical">
          <Form.Item
            label="Points to Redeem"
            help={`You have ${balance?.currentPoints || 0} points available`}
          >
            <Input
              type="number"
              placeholder="Enter number of points"
              value={redeemAmount}
              onChange={(e) => setRedeemAmount(e.target.value)}
              min={1}
              max={balance?.currentPoints || 0}
            />
          </Form.Item>
          <Form.Item label="Estimated Credit">
            <Text strong style={{ fontSize: 18 }}>
              ${((parseInt(redeemAmount) || 0) * 0.01).toFixed(2)}
            </Text>
            <Text type="secondary"> (1 point = $0.01)</Text>
          </Form.Item>
        </Form>
      </Modal>
    </PublicLayout>
  );
};

export default CustomerRewards;
